import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, Tag, CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function TermsModal({ open, onOpenChange, onAccept, planName }) {
  const [agreed, setAgreed] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState(null); // null, 'validating', 'valid', 'invalid'
  const [couponDetails, setCouponDetails] = useState(null);
  const [couponError, setCouponError] = useState("");

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponStatus("validating");
    setCouponError("");
    setCouponDetails(null);
    
    try {
      const response = await axios.post(`${API}/billing/validate-coupon`, {
        coupon_code: couponCode.trim()
      });
      
      if (response.data.valid) {
        setCouponStatus("valid");
        setCouponDetails(response.data);
      } else {
        setCouponStatus("invalid");
        setCouponError(response.data.error || "Invalid coupon code");
      }
    } catch (error) {
      setCouponStatus("invalid");
      setCouponError(error.response?.data?.detail || "Could not validate coupon");
    }
  };

  const handleAccept = () => {
    if (agreed) {
      // Only pass coupon if it was validated successfully
      const validCoupon = couponStatus === "valid" ? couponCode.trim() : null;
      onAccept(validCoupon);
      setAgreed(false);
      setCouponCode("");
      setCouponStatus(null);
      setCouponDetails(null);
    }
  };

  const handleClose = () => {
    setAgreed(false);
    setCouponCode("");
    setCouponStatus(null);
    setCouponDetails(null);
    setCouponError("");
    onOpenChange(false);
  };

  const handleCouponChange = (e) => {
    setCouponCode(e.target.value.toUpperCase());
    // Reset validation when code changes
    if (couponStatus) {
      setCouponStatus(null);
      setCouponDetails(null);
      setCouponError("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Terms & Conditions
          </DialogTitle>
          <DialogDescription>
            Please review and accept our terms before subscribing to {planName || "CarePilot"}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-6 text-sm text-slate-700">
            <section>
              <h3 className="font-bold text-slate-900 mb-2">CarePilot USA – Terms and Conditions</h3>
              <p className="text-xs text-slate-500 mb-4">Effective Date: January 2026</p>
              
              <p className="mb-4">
                These Terms and Conditions ("Terms") govern access to and use of the CarePilot USA Home Care Software Platform 
                ("CarePilot," "Platform," "Service," "we," "our," or "us"). CarePilot USA is a company operating in the United States.
              </p>
              
              <p className="mb-4 font-medium">
                By creating an account, clicking "I Agree," or submitting payment, you ("Customer," "you," or "your") acknowledge 
                that you have read, understood, and agree to be legally bound by these Terms.
              </p>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">1. Eligibility & Authority</h4>
              <p>By using the Platform, you represent and warrant that:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>You are at least 18 years old.</li>
                <li>You have the legal authority to bind the home care agency or organization you represent.</li>
                <li>All registration and billing information provided is accurate, current, and complete.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">2. Scope of Services</h4>
              <p>CarePilot USA provides software-as-a-service (SaaS) tools for non-medical and medical home care agencies, which may include:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Scheduling and staffing</li>
                <li>Electronic Visit Verification (EVV)</li>
                <li>Caregiver and client management</li>
                <li>Time tracking, billing, and reporting</li>
              </ul>
              <p className="mt-2 font-medium">CarePilot USA does not provide medical care, clinical services, legal advice, or regulatory certification.</p>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">3. Account Registration & Security</h4>
              <ul className="list-disc ml-5 space-y-1">
                <li>Each customer must maintain a unique account.</li>
                <li>You are responsible for all activity conducted under your login credentials.</li>
                <li>You must notify CarePilot USA immediately of any unauthorized use.</li>
                <li>Account sharing or resale of access is strictly prohibited.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">4. Subscription Plans & Client Limits</h4>
              <p>CarePilot USA subscriptions are based on active client count:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li><strong>Tier 1 (Starter):</strong> Up to 10 active clients - $149/month</li>
                <li><strong>Tier 2 (Growth):</strong> 11–49 active clients - $349/month</li>
                <li><strong>Tier 3 (Enterprise):</strong> 50 or more active clients - $549/month</li>
              </ul>
              <p className="mt-2">If your active client count exceeds your subscribed tier, your plan may be automatically upgraded, additional charges may apply, or access may be restricted until compliance is restored.</p>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">5. Fees, Billing & Payment</h4>
              <ul className="list-disc ml-5 space-y-1">
                <li>All fees are billed in advance monthly.</li>
                <li>No free trials are offered.</li>
                <li>All prices are listed in U.S. Dollars (USD).</li>
                <li>You authorize CarePilot USA to charge your selected payment method.</li>
                <li>Failure to pay may result in suspension or termination of service.</li>
              </ul>
            </section>

            <section className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h4 className="font-semibold text-red-900 mb-2">6. No Refund Policy</h4>
              <p className="text-red-800">To the fullest extent permitted by U.S. law:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1 text-red-800">
                <li>All subscription fees, setup fees, and add-ons are <strong>non-refundable</strong>.</li>
                <li>No refunds or credits are issued for partial months, unused services, or downgrades.</li>
                <li>Cancellation does not relieve you of payment obligations already incurred.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">7. Customer Responsibilities</h4>
              <p>You agree that you are solely responsible for:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Compliance with federal, state, and local U.S. laws, including home care, labor, EVV, HIPAA (if applicable), and privacy regulations.</li>
                <li>Accuracy of caregiver, client, payroll, and billing data.</li>
                <li>Obtaining required consents from clients and caregivers.</li>
                <li>Proper use of the Platform in accordance with applicable laws.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">8. Prohibited Use</h4>
              <p>You may not:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Use the Platform for unlawful, fraudulent, or deceptive purposes.</li>
                <li>Reverse engineer, copy, modify, or resell the Platform.</li>
                <li>Upload malicious software or attempt to breach system security.</li>
                <li>Interfere with the performance or availability of the Platform.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">9. Data Ownership & Privacy</h4>
              <ul className="list-disc ml-5 space-y-1">
                <li>You retain ownership of all data you upload to the Platform.</li>
                <li>CarePilot USA processes data solely to provide the Service.</li>
                <li>Reasonable administrative, technical, and physical safeguards are used to protect data.</li>
                <li>You are responsible for ensuring lawful data collection and consent.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">10. Service Availability</h4>
              <ul className="list-disc ml-5 space-y-1">
                <li>CarePilot USA strives for high availability but does not guarantee uninterrupted service.</li>
                <li>Scheduled maintenance and emergency outages may occur.</li>
                <li>CarePilot USA is not liable for service interruptions beyond its reasonable control.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">11. Third-Party Integrations</h4>
              <p>The Platform may integrate with third-party services. CarePilot USA is not responsible for:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Third-party system failures or outages</li>
                <li>Data handled by external providers</li>
                <li>Changes or discontinuation of third-party services</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">12. Suspension & Termination</h4>
              <p>CarePilot USA may suspend or terminate access if:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Payments are overdue</li>
                <li>These Terms are violated</li>
                <li>Use poses legal, security, or operational risk</li>
              </ul>
              <p className="mt-2">You may cancel your subscription at any time. Cancellation becomes effective at the end of the current billing period.</p>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">13. Limitation of Liability</h4>
              <p>To the maximum extent permitted under U.S. law:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>CarePilot USA is not liable for indirect, incidental, or consequential damages.</li>
                <li>Total liability shall not exceed the amount paid by you in the 12 months preceding the claim.</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">14. Indemnification</h4>
              <p>You agree to indemnify and hold harmless CarePilot USA from any claims, damages, or liabilities arising from:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Your use of the Platform</li>
                <li>Violation of laws or regulations</li>
                <li>Data you upload or manage through the Service</li>
              </ul>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">15. Modifications to Terms</h4>
              <p>CarePilot USA may update these Terms from time to time. Continued use of the Platform after updates constitutes acceptance of the revised Terms.</p>
            </section>

            <section>
              <h4 className="font-semibold text-slate-900 mb-2">16. Governing Law</h4>
              <p>These Terms shall be governed by and construed under the laws of the United States, without regard to conflict-of-law principles.</p>
            </section>

            <section className="bg-slate-100 p-4 rounded-lg">
              <h4 className="font-semibold text-slate-900 mb-2">17. Acceptance</h4>
              <p>By clicking "I Agree," creating an account, or submitting payment, you confirm that you have read, understood, and agreed to these Terms and Conditions.</p>
            </section>
          </div>
        </ScrollArea>

        <div className="border-t pt-4 mt-4">
          {/* Coupon Code Input */}
          <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <Label htmlFor="coupon-code" className="flex items-center gap-2 text-sm font-medium mb-2">
              <Tag className="w-4 h-4 text-primary" />
              Have a promo code?
            </Label>
            <div className="flex gap-2">
              <Input
                id="coupon-code"
                placeholder="Enter coupon code (optional)"
                value={couponCode}
                onChange={handleCouponChange}
                className="uppercase flex-1"
                data-testid="coupon-code-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={validateCoupon}
                disabled={!couponCode.trim() || couponStatus === "validating"}
                data-testid="validate-coupon-btn"
              >
                {couponStatus === "validating" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Apply"
                )}
              </Button>
            </div>
            
            {/* Coupon validation result */}
            {couponStatus === "valid" && couponDetails && (
              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-sm text-emerald-700">
                  <strong>{couponDetails.discount}</strong> - {couponDetails.name}
                  {couponDetails.duration === "once" && " (first month)"}
                  {couponDetails.duration === "repeating" && ` (${couponDetails.duration_in_months} months)`}
                  {couponDetails.duration === "forever" && " (forever)"}
                </span>
              </div>
            )}
            
            {couponStatus === "invalid" && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-700">{couponError}</span>
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-1">
              {couponStatus !== "valid" && "Click Apply to validate, or enter on the payment page"}
            </p>
          </div>

          <div className="flex items-start space-x-3 mb-4">
            <Checkbox 
              id="terms-agree" 
              checked={agreed} 
              onCheckedChange={setAgreed}
              data-testid="terms-checkbox"
            />
            <label 
              htmlFor="terms-agree" 
              className="text-sm leading-tight cursor-pointer"
            >
              By clicking "Agree & Continue," I acknowledge that I am entering into a legally binding agreement 
              with CarePilot USA and authorize charges according to my selected plan. I understand there are{" "}
              <strong className="text-red-600">no refunds</strong>.
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleAccept} 
              disabled={!agreed}
              data-testid="accept-terms-btn"
            >
              Agree & Continue to Payment
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TermsModal;
